import React, { memo } from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemText from '@material-ui/core/ListItemText';
import Select from '@material-ui/core/Select';
import Chip from '@material-ui/core/Chip';
import CustomTextField from 'global/react-mui/atoms/InputField/styles';
import FormControl from 'global/react-mui/atoms/FormControl';
import Checkbox from 'global/react-mui/atoms/Checkbox';
import ArrowDown from 'global/react-mui/atoms/Select/ArrowDown';
import { MultiSelectProps } from './types';
import { colors } from 'global/react-mui/theme/colors';
import { INPUT_HEIGHT } from 'global/react-mui/theme/constants';
import { Typography } from '@material-ui/core';

const MultipleSelect: React.FC<MultiSelectProps> = ({
	options,
	id,
	selectedOptions,
	label,
	disabled,
	formControlProps,
	placeholder,
	inputFieldProps,
	renderOptions,
	handleSelectChange,
}) => {
	const classes = useStyles();
	const getItemLabel = (value): string => {
		const option = options?.find((option) => option.value === value);
		return option?.label || '';
	};

	return (
		<FormControl {...formControlProps}>
			{label && (
				<InputLabel htmlFor={id} shrink style={{ transform: 'translate(0px, -16px) scale(0.75)' }} className={classes.label}>
					{label}
				</InputLabel>
			)}
			<Select
				id={id}
				multiple
				displayEmpty
				disabled={disabled}
				value={selectedOptions?.map((value) => value)}
				onChange={(e) => handleSelectChange(e.target.value as string)}
				input={<CustomTextField {...inputFieldProps} />}
				renderValue={(selected) => {
					if ((selected as string[]).length === 0) {
						return (
							<Typography color="textSecondary" variant="body1" className={classes.placeholder}>
								{placeholder}
							</Typography>
						);
					}
					return (
						<div className={classes.chips}>
							{(selected as string[]).slice(0, 2).map((value) => (
								<Chip size="small" key={value} label={getItemLabel(value)} className={classes.chip} />
							))}
							{(selected as string[]).length > 2 ? <Chip size="small" label="..." className={classes.chip} /> : null}
						</div>
					);
				}}
				inputProps={{ 'aria-label': label, height: INPUT_HEIGHT }}
				IconComponent={({ className }) => <ArrowDown fontSize="small" className={className} />}
				MenuProps={{
					anchorOrigin: {
						vertical: 'bottom',
						horizontal: 'left',
					},
					transformOrigin: {
						vertical: 'top',
						horizontal: 'left',
					},
					getContentAnchorEl: null,
				}}>
				{renderOptions
					? options.map(renderOptions)
					: options.map((option) => (
							<MenuItem key={option.label} value={option.value}>
								<Checkbox checked={selectedOptions?.includes(option.value)} />
								<ListItemText primary={option.label} />
							</MenuItem>
				))}
			</Select>
		</FormControl>
	);
};

MultipleSelect.defaultProps = {
	placeholder: 'Choose your options...',
	formControlProps: { width: '340px', marginLeft: 0, marginRight: 0 },
	disabled: false,
	inputFieldProps: {},
};

export const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		label: {
			whiteSpace: 'nowrap',
		},
		chips: {
			display: 'flex',
			flexWrap: 'nowrap',
			overflowX: 'hidden',
		},
		chip: {
			marginRight: 8,
			paddingLeft: 4,
			paddingRight: 4,
			background: colors['lighter-grey'],
			borderRadius: 4,
		},
		placeholder: {
			opacity: 0.7,
		},
	}),
);

export default memo(MultipleSelect);
